/**
 * Program launcher (P4) — one tile per granted module; single-program users
 * bypass straight into the module (product-owner decision 2026-07-12).
 * Sits OUTSIDE PracticeGate: program selection precedes practice selection.
 */
import { Navigate, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useProgramAccess } from '@/platform/ProgramAccessContext';
import { useProgramYear } from '@/contexts/ProgramYearContext';

export default function ProgramLauncher() {
  const { grantedModules, loading } = useProgramAccess();
  const { programYear } = useProgramYear();
  const navigate = useNavigate();

  if (loading) return <div className="p-8 text-muted-foreground">Loading your programs…</div>;

  const visible = grantedModules.filter(m => m.supportedYears(programYear));

  // Single-program bypass (product-owner decision 2026-07-12)
  if (visible.length === 1) return <Navigate to={visible[0].homeRoute} replace />;

  if (visible.length === 0) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center">
        <h1 className="text-xl font-semibold">No programs assigned</h1>
        <p className="mt-2 text-muted-foreground">
          Your account has no program access for {programYear}. Contact your VBCA
          administrator to be granted access.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl py-10">
      <h1 className="text-2xl font-semibold">Your programs</h1>
      <p className="mt-1 text-muted-foreground">Choose a program to work in.</p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {visible.map(m => (
          <Card
            key={m.code}
            className="cursor-pointer transition-shadow hover:shadow-md"
            onClick={() => navigate(m.homeRoute)}
            role="button"
            aria-label={`Open ${m.displayName}`}
          >
            <CardHeader>
              <CardTitle>{m.displayName}</CardTitle>
              <CardDescription>
                {m.code === 'mips'
                  ? 'Quality measure tracking, gap management, and MIPS scoring.'
                  : 'MSSP ACO APP Plus reporting, QPS management, and MIPS exposure.'}
              </CardDescription>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground">Open →</CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
